import { useQuery } from "@tanstack/react-query";
import { fetchDashboardInsights } from "../../api/queries";
import { useLanguage } from "../../contexts/LanguageContext";
import { absoluteChange } from "../../utils/percentChange";
import BarList from "./components/BarList";
import HealthSegments from "./components/HealthSegments";
import Heatmap from "./components/Heatmap";
import { CardsBreakdown, ComingUp, RewardFunnel, StaffTable } from "./components/InsightsBlocks";
import KpiCard from "./components/KpiCard";
import LockedPreview from "./components/LockedPreview";
import { DiffPill, Panel, SectionError, Skeleton } from "./components/ui";
import { formatNumber, formatPercent } from "./format";
import { sampleInsights } from "./sampleData";

// The Growth plan tab. When the plan lacks it, the same view renders sample
// data under a lock card and the insights endpoint is never requested.
export default function Insights({
  storeId,
  range,
  locked,
  teaser,
  timeZone,
  canPush,
  canExport,
  onCreatePush,
}) {
  const { t, language } = useLanguage();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["dashboard", "insights", storeId, range],
    queryFn: () => fetchDashboardInsights(storeId, range),
    enabled: !locked && !!storeId,
  });

  if (locked) {
    const atRisk = teaser?.insights?.at_risk;
    return (
      <LockedPreview
        title={t("dashboard.locked.insights.title")}
        body={
          Number.isFinite(atRisk) && atRisk > 0
            ? t("dashboard.locked.insights.body", { value: formatNumber(atRisk, language) })
            : t("dashboard.locked.insights.generic")
        }
        upgradeLabel={t("dashboard.locked.insights.upgrade")}
      >
        <InsightsView data={sampleInsights} preview canPush timeZone={timeZone} />
      </LockedPreview>
    );
  }

  if (isError) return <SectionError onRetry={() => refetch()} />;

  return (
    <InsightsView
      storeId={storeId}
      data={data}
      loading={isLoading || !data}
      timeZone={timeZone}
      canPush={canPush}
      canExport={canExport}
      onCreatePush={onCreatePush}
    />
  );
}

export function InsightsView({
  storeId,
  data,
  loading = false,
  preview = false,
  timeZone,
  canPush = false,
  canExport = false,
  onCreatePush,
}) {
  const { t, language } = useLanguage();
  const retention = data?.retention || {};
  const rangeDays = data?.range_days;
  const atRisk = data?.health?.at_risk;

  const pts = (cur, prev) => {
    const diff = absoluteChange(cur, prev);
    return diff === null ? null : Math.round(diff * 100);
  };
  const repeatDiff = pts(retention.repeat_rate?.current, retention.repeat_rate?.previous);
  const redemptionDiff = pts(retention.redemption_rate?.current, retention.redemption_rate?.previous);
  const gapDiff = absoluteChange(
    retention.median_days_between_visits?.current,
    retention.median_days_between_visits?.previous,
  );
  const atRiskDiff = absoluteChange(retention.at_risk?.current, retention.at_risk?.previous);

  const kpis = [
    {
      key: "repeat_rate",
      value: formatPercent(retention.repeat_rate?.current, language),
      pill: (
        <DiffPill
          diff={repeatDiff}
          text={t("dashboard.delta.points", { value: Math.abs(repeatDiff ?? 0) })}
        />
      ),
    },
    {
      key: "median_days_between_visits",
      value: formatNumber(retention.median_days_between_visits?.current, language, {
        maximumFractionDigits: 1,
      }),
      pill: (
        <DiffPill
          diff={gapDiff === null ? null : Math.round(gapDiff * 10) / 10}
          higherIsBetter={false}
          text={t("dashboard.delta.days", {
            value: formatNumber(Math.abs(gapDiff ?? 0), language, { maximumFractionDigits: 1 }),
          })}
        />
      ),
    },
    {
      key: "redemption_rate",
      value: formatPercent(retention.redemption_rate?.current, language),
      pill: (
        <DiffPill
          diff={redemptionDiff}
          text={t("dashboard.delta.points", { value: Math.abs(redemptionDiff ?? 0) })}
        />
      ),
    },
    {
      key: "at_risk",
      value: formatNumber(retention.at_risk?.current, language),
      pill: (
        <DiffPill
          diff={atRiskDiff}
          higherIsBetter={false}
          text={formatNumber(Math.abs(atRiskDiff ?? 0), language)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {loading ? (
        <Skeleton className="h-5 w-full max-w-xl" />
      ) : (
        <p className="max-w-2xl text-[15px] leading-relaxed text-gray-700">
          {atRisk > 0
            ? t("dashboard.insights.summary", { value: formatNumber(atRisk, language) })
            : t("dashboard.insights.summaryNone")}
        </p>
      )}

      <section
        aria-label={t("dashboard.insights.kpisLabel")}
        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
      >
        {kpis.map((k) => (
          <KpiCard
            key={k.key}
            loading={loading}
            label={t(`dashboard.insights.kpis.${k.key}.label`)}
            value={k.value}
            pill={k.pill}
            footnote={t(`dashboard.insights.kpis.${k.key}.hint`)}
          />
        ))}
      </section>

      <div className="flex flex-wrap items-stretch gap-4">
        <HealthSegments
          storeId={storeId}
          health={data?.health}
          loading={loading}
          preview={preview}
          canPush={canPush}
          canExport={canExport}
          onCreatePush={onCreatePush}
        />
        <RewardFunnel funnel={data?.funnel} loading={loading} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Panel title={t("dashboard.insights.topRewards.title")}>
          <BarList
            loading={loading}
            label={t("dashboard.insights.topRewards.title")}
            items={(data?.top_rewards || []).map((r) => ({
              key: r.campaign_id,
              name: r.name,
              value: r.count,
              display: formatNumber(r.count ?? 0, language),
            }))}
          />
        </Panel>
        <StaffTable staff={data?.staff} loading={loading} />
        <CardsBreakdown cards={data?.cards} rangeDays={rangeDays} loading={loading} />
      </div>

      <div className="flex flex-wrap items-stretch gap-4">
        <Heatmap cells={data?.heatmap} timeZone={timeZone} loading={loading} />
        <ComingUp
          upcoming={data?.upcoming}
          loading={loading}
          canPush={canPush}
          onExpiryReminder={() => onCreatePush?.("expiring_30d")}
        />
      </div>
    </div>
  );
}
